import { Loader2 } from 'lucide-react';

import filledCheckIcon from '@/assets/icons/filledCheckIcon.svg';
import uploadFailedIcon from '@/assets/icons/uploadFailedIcon.svg';
import uploadRetryIcon from '@/assets/icons/uploadRetryIcon.svg';
import { getFileIcon } from '@/utils/fileUtils';
import { cn } from '@/utils/utils';

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed';

export interface NarrativeUploadFile {
  file: File;
  id: string;
  name: string;
  status: UploadStatus;
}

interface NarrativeUploadFileCardProps {
  className?: string;
  file: NarrativeUploadFile;
  onRetry?: (fileId: string) => void;
}

const getStatusIcon = (status: UploadStatus, onRetry?: () => void) => {
  switch (status) {
    case 'completed':
      return <img alt="Completed" className="h-4 w-4" src={filledCheckIcon} />;
    case 'uploading':
    case 'pending':
      return <Loader2 className="text-neutral-mid h-4 w-4 animate-spin" />;
    case 'failed':
      return (
        <div className="flex items-center gap-1.5">
          <img alt="Failed" className="h-4 w-4" src={uploadFailedIcon} />
          {onRetry && (
            <button
              aria-label="Retry upload"
              className="cursor-pointer transition-opacity hover:opacity-70"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              <img alt="Retry" className="icon-themed h-4 w-4" src={uploadRetryIcon} />
            </button>
          )}
        </div>
      );
    default:
      return null;
  }
};

export function NarrativeUploadFileCard({
  className,
  file,
  onRetry,
}: NarrativeUploadFileCardProps) {
  const fileIcon = getFileIcon(file.name);

  const handleRetry = () => {
    onRetry?.(file.id);
  };

  return (
    <div
      className={cn(
        'my-1 flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md py-1 transition-colors hover:bg-back-bg2',
        className,
      )}
    >
      {/* File Icon */}
      <div className="bg-back-bg2 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded">
        <img alt="File type" className="h-5 w-5" src={fileIcon} />
      </div>

      {/* File Name */}
      <div className="min-w-0 flex-1">
        <p className="text-secondary truncate font-['Inter'] text-[12px] leading-[160%] font-normal tracking-normal">
          {file.name}
        </p>
      </div>

      {/* Status Icon */}
      <div className="flex-shrink-0">
        {getStatusIcon(file.status, file.status === 'failed' ? handleRetry : undefined)}
      </div>
    </div>
  );
}
